import { z } from 'zod';

/* ─────────── Исполнительная документация (ДК-06, раздел 6.15) ─────────── */

const DateStr = z.iso.date();

/** Виды документов перечня */
export const EXEC_DOC_KINDS = {
  hidden_work_act: 'Акт освидетельствования скрытых работ',
  critical_structure_act: 'Акт приёмки ответственных конструкций',
  exec_scheme: 'Исполнительная схема',
  journal: 'Журнал работ',
  test_protocol: 'Протокол испытаний',
  passport: 'Паспорт',
  certificate: 'Сертификат',
  other: 'Другой документ',
} as const;
export type ExecDocKind = keyof typeof EXEC_DOC_KINDS;
const KIND = z.enum(Object.keys(EXEC_DOC_KINDS) as [ExecDocKind, ...ExecDocKind[]]);

/** missing — не хватает, ready — есть (файл или подписанный акт), na — не требуется (с причиной) */
export const EXEC_DOC_STATUSES = { missing: 'Не хватает', ready: 'Готово', na: 'Не требуется' } as const;
export type ExecDocStatus = keyof typeof EXEC_DOC_STATUSES;

export const ExecTemplateInput = z.object({
  title: z.string().trim().min(3).max(200),
  /** Вид работ: монолит, сварка, земляные и т. п. — свободный короткий код */
  workKind: z.string().trim().min(2).max(40).default('other'),
  items: z
    .array(
      z.object({
        kind: KIND,
        title: z.string().trim().min(3).max(300),
        normRef: z.string().trim().max(300).nullable().optional(),
        required: z.boolean().optional(),
      }),
    )
    .min(1)
    .max(200),
});
export type ExecTemplateInput = z.infer<typeof ExecTemplateInput>;

export const ExecTemplate = z.object({
  id: z.uuid(),
  title: z.string(),
  workKind: z.string(),
  status: z.enum(['active', 'archived']),
  items: z.array(
    z.object({
      id: z.uuid(),
      position: z.number().int(),
      kind: KIND,
      title: z.string(),
      normRef: z.string().nullable(),
      required: z.boolean(),
    }),
  ),
});
export type ExecTemplate = z.infer<typeof ExecTemplate>;

export const ExecApplyInput = z.object({
  templateId: z.uuid(),
  objectId: z.uuid().nullable().optional(),
  /** Работа графика, к которой относится перечень (чего не хватает к какой работе) */
  activityId: z.uuid().nullable().optional(),
});
export type ExecApplyInput = z.infer<typeof ExecApplyInput>;

export const ExecItemInput = z.object({
  kind: KIND,
  title: z.string().trim().min(3).max(300),
  normRef: z.string().trim().max(300).nullable().optional(),
  objectId: z.uuid().nullable().optional(),
  activityId: z.uuid().nullable().optional(),
  required: z.boolean().optional(),
  responsibleId: z.uuid().nullable().optional(),
  dueDate: DateStr.nullable().optional(),
});
export type ExecItemInput = z.infer<typeof ExecItemInput>;

export const ExecItemPatch = z.object({
  status: z.enum(['missing', 'ready', 'na']).optional(),
  /** Подписанный акт освидетельствования из надзора вместо файла (ТН-03) */
  inspectionId: z.uuid().nullable().optional(),
  naReason: z.string().trim().max(500).nullable().optional(),
  responsibleId: z.uuid().nullable().optional(),
  dueDate: DateStr.nullable().optional(),
});
export type ExecItemPatch = z.infer<typeof ExecItemPatch>;

export const ExecItem = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  objectId: z.uuid().nullable(),
  objectName: z.string().nullable(),
  activityId: z.uuid().nullable(),
  activityName: z.string().nullable(),
  kind: KIND,
  title: z.string(),
  normRef: z.string().nullable(),
  required: z.boolean(),
  status: z.enum(['missing', 'ready', 'na']),
  naReason: z.string().nullable(),
  filesCount: z.number().int(),
  inspectionId: z.uuid().nullable(),
  inspectionNumber: z.string().nullable(),
  responsibleId: z.uuid().nullable(),
  responsibleName: z.string().nullable(),
  dueDate: z.string().nullable(),
  overdue: z.boolean(),
  canEdit: z.boolean(),
});
export type ExecItem = z.infer<typeof ExecItem>;

export const ExecSummary = z.object({
  /** Обязательных документов без «не требуется» */
  total: z.number().int(),
  ready: z.number().int(),
  missing: z.number().int(),
  /** Готовность, % (0, если обязательных нет) */
  percent: z.number().int(),
});
export type ExecSummary = z.infer<typeof ExecSummary>;

export const ExecOverview = z.object({
  summary: ExecSummary,
  /** Чего не хватает к какой работе: по работам графика и «без работы» */
  byWork: z.array(
    z.object({
      activityId: z.uuid().nullable(),
      activityName: z.string().nullable(),
      summary: ExecSummary,
    }),
  ),
  items: z.array(ExecItem),
  canEdit: z.boolean(),
});
export type ExecOverview = z.infer<typeof ExecOverview>;

/** Готовность по обязательным документам: «не требуется» не считается, необязательные — тоже */
export function execSummary(items: Array<{ required: boolean; status: ExecDocStatus }>): ExecSummary {
  const counted = items.filter((i) => i.required && i.status !== 'na');
  const ready = counted.filter((i) => i.status === 'ready').length;
  return {
    total: counted.length,
    ready,
    missing: counted.length - ready,
    percent: counted.length ? Math.floor((ready * 100) / counted.length) : 0,
  };
}

/* ─────────── Опись пакета исполнительной документации (ДК-08) ─────────── */

export interface ExecInventoryFile {
  fileId: string;
  name: string;
  size: number;
}

/** Строка описи: только готовые документы, с общим номером по порядку */
export interface ExecInventoryEntry {
  no: number;
  itemId: string;
  kind: ExecDocKind;
  title: string;
  normRef: string | null;
  activityName: string | null;
  objectName: string | null;
  /** Подписанный акт освидетельствования вместо файла */
  inspectionNumber: string | null;
  files: ExecInventoryFile[];
}

export interface ExecInventory {
  projectId: string;
  projectCode: string;
  projectName: string;
  entries: ExecInventoryEntry[];
  /** Обязательные документы, которых в пакете ещё нет */
  gaps: Array<{ itemId: string; title: string; activityName: string | null; overdue: boolean }>;
  summary: ExecSummary;
  /** Пакет можно передавать, когда все обязательные документы готовы */
  complete: boolean;
}

/* ─────────── Передача пакета по сопроводительной накладной (ДК-09) ─────────── */

export const ExecTransmittalInput = z.object({
  recipient: z.string().trim().min(2).max(200),
  counterpartyId: z.uuid().nullable().optional(),
  sentOn: z.iso.date(),
  note: z.string().trim().max(1000).nullable().optional(),
  /** Какие готовые документы передаются; пусто — вся опись объекта или проекта */
  itemIds: z.array(z.uuid()).max(500).optional(),
  objectId: z.uuid().nullable().optional(),
});
export type ExecTransmittalInput = z.infer<typeof ExecTransmittalInput>;

export const ExecTransmittalReceive = z.object({
  receivedOn: z.iso.date(),
  receivedBy: z.string().trim().min(2).max(200),
});
export type ExecTransmittalReceive = z.infer<typeof ExecTransmittalReceive>;

export interface ExecTransmittalEntry {
  no: number;
  itemId: string;
  title: string;
  /** Файлы на момент передачи: что именно ушло (версия пакета) */
  files: Array<{ fileId: string; name: string; size: number }>;
}

export interface ExecTransmittal {
  id: string;
  projectId: string;
  /** ПН-0001: сквозной внутри проекта */
  numberLabel: string;
  number: number;
  recipient: string;
  counterpartyId: string | null;
  counterpartyName: string | null;
  sentOn: string;
  note: string | null;
  status: 'sent' | 'received';
  receivedOn: string | null;
  receivedBy: string | null;
  entries: ExecTransmittalEntry[];
  /** Передано, но получение не подтверждено дольше срока */
  waitingDays: number | null;
}

export const execTransmittalNumber = (n: number) => `ПН-${String(n).padStart(4, '0')}`;
/** Сколько дней ждём подтверждения получения, прежде чем напомнить */
export const TRANSMITTAL_REMIND_DAYS = 5;
